/*
 * Copyright (c) 2026 Gabriele Bonora. Tutti i diritti riservati.
 *
 * Software proprietario. Nessuna licenza d'uso, copia, modifica o distribuzione
 * e' concessa in assenza di autorizzazione scritta del titolare dei diritti.
 * Vedere il file LICENSE nella radice del progetto.
 */

/*
 * Segnalazioni: bug, miglioramenti e funzionalita' nuove (migrazione 015).
 *
 * Chi usa il gestionale scrive qui cosa non va o cosa manca, anche a voce; chi
 * lo sviluppa le prende in carico e le porta a "completata" o "rifiutata" con il
 * motivo. L'elenco diventa la lista di lavoro: si guarda quella per decidere dove
 * intervenire, non la memoria delle telefonate.
 */
import { useEffect, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Plus, X, Bug, Sparkles, Lightbulb, Mic, Image as ImageIcon, Trash2, Send, Search, Paperclip,
} from 'lucide-react';
import AdminLayout from '../components/AdminLayout';
import RegistratoreAudio from '../components/RegistratoreAudio';
import FeedbackClienti from '../components/FeedbackClienti';
import { segnalazioniAPI } from '../api/segnalazioni';
import { fmtDataOra } from '../utils/date';

const TIPI = {
  bug:           { label: 'Bug',           icon: Bug,       color: '#B91C1C', bg: '#FEE2E2', spiega: 'Qualcosa non funziona come dovrebbe' },
  miglioramento: { label: 'Miglioramento', icon: Sparkles,  color: '#1D4ED8', bg: '#DBEAFE', spiega: 'Funziona, ma si potrebbe fare meglio' },
  funzionalita:  { label: 'Nuova funzionalità', icon: Lightbulb, color: '#047857', bg: '#D1FAE5', spiega: "Una cosa che oggi non c'è" },
};

const STATI = {
  nuova:           { label: 'Nuova',           color: '#374151', bg: '#F3F4F6' },
  presa_in_carico: { label: 'Presa in carico', color: '#6D28D9', bg: '#EDE9FE' },
  in_corso:        { label: 'In corso',        color: '#B45309', bg: '#FEF3C7' },
  completata:      { label: 'Completata',      color: '#065F46', bg: '#D1FAE5' },
  rifiutata:       { label: 'Rifiutata',       color: '#991B1B', bg: '#FEE2E2' },
};

const PRIORITA = {
  bassa:   { label: 'Bassa',   color: '#6B7280' },
  normale: { label: 'Normale', color: '#3730A3' },
  alta:    { label: 'Alta',    color: '#C2410C' },
  urgente: { label: 'Urgente', color: '#B91C1C' },
};

const LINGUETTE = [
  { id: 'aperte', label: 'Aperte' },
  { id: 'nuova', label: 'Nuove' },
  { id: 'presa_in_carico', label: 'Prese in carico' },
  { id: 'in_corso', label: 'In corso' },
  { id: 'completata', label: 'Completate' },
  { id: 'rifiutata', label: 'Rifiutate' },
];

// Da dove arriva: l'admin stesso, un tecnico dal campo, un cliente (migrazione 016).
const ORIGINI = {
  admin:   { label: 'Ufficio',  color: '#4B5563', bg: '#F3F4F6' },
  tecnico: { label: 'Tecnico',  color: '#0369A1', bg: '#E0F2FE' },
  cliente: { label: 'Cliente',  color: '#9D174D', bg: '#FCE7F3' },
};

const errMsg = (e, def) => e?.response?.data?.error || def;

export default function Segnalazioni() {
  const [sezione, setSezione] = useState('segnalazioni'); // segnalazioni | questionario
  const [stato, setStato] = useState('aperte');
  const [tipo, setTipo] = useState('');
  const [origine, setOrigine] = useState('');
  const [q, setQ] = useState('');
  const [cerca, setCerca] = useState('');
  const [nuova, setNuova] = useState(false);
  const [aperta, setAperta] = useState(null);

  // La ricerca parte quando si smette di scrivere, non a ogni lettera.
  useEffect(() => {
    const t = setTimeout(() => setCerca(q), 300);
    return () => clearTimeout(t);
  }, [q]);

  const { data, isLoading, error } = useQuery({
    queryKey: ['segnalazioni', stato, tipo, origine, cerca],
    queryFn: () => segnalazioniAPI.list({ stato, tipo: tipo || undefined, origine: origine || undefined, q: cerca || undefined }),
    enabled: sezione === 'segnalazioni',
  });
  const contatori = data?.contatori || {};

  return (
    <AdminLayout title="Segnalazioni">
      <div style={s.sezioni}>
        <button style={{ ...s.sezioneBtn, ...(sezione === 'segnalazioni' ? s.sezioneAttiva : {}) }} onClick={() => setSezione('segnalazioni')}>
          Segnalazioni
        </button>
        <button style={{ ...s.sezioneBtn, ...(sezione === 'questionario' ? s.sezioneAttiva : {}) }} onClick={() => setSezione('questionario')}>
          Questionario «Dicci la tua»
        </button>
      </div>

      {sezione === 'questionario' ? (
        <FeedbackClienti onApriSegnalazione={(id) => { setSezione('segnalazioni'); setAperta(id); }} />
      ) : (<>
      <div style={s.intro}>
        <p style={s.introTesto}>
          Segnala un bug, chiedi un miglioramento o proponi una funzionalità nuova. Puoi
          spiegarlo anche a voce e allegare una foto dello schermo. Ogni richiesta diventa un
          task con il suo stato, così si vede cosa è in lavorazione e cosa è stato deciso.
        </p>
        <button style={s.btnPrimario} onClick={() => setNuova(true)}>
          <Plus size={16} /> Nuova segnalazione
        </button>
      </div>

      <div style={s.linguette}>
        {LINGUETTE.map((l) => (
          <button key={l.id} onClick={() => setStato(l.id)} style={{ ...s.linguetta, ...(stato === l.id ? s.linguettaAttiva : {}) }}>
            {l.label}
            <span style={s.conteggio}>{contatori[l.id] ?? 0}</span>
          </button>
        ))}
      </div>

      <div style={s.filtri}>
        <div style={s.cercaBox}>
          <Search size={15} color="#9CA3AF" />
          <input style={s.cercaInput} placeholder="Cerca nel titolo, nella descrizione o nell'area" value={q} onChange={(e) => setQ(e.target.value)} />
        </div>
        <select style={s.select} value={tipo} onChange={(e) => setTipo(e.target.value)}>
          <option value="">Tutti i tipi</option>
          {Object.entries(TIPI).map(([k, t]) => <option key={k} value={k}>{t.label}</option>)}
        </select>
        <select style={s.select} value={origine} onChange={(e) => setOrigine(e.target.value)}>
          <option value="">Da chiunque</option>
          {Object.entries(ORIGINI).map(([k, o]) => <option key={k} value={k}>Da: {o.label}</option>)}
        </select>
      </div>

      <div style={s.elenco}>
        {isLoading ? (
          <div style={s.vuoto}>Caricamento...</div>
        ) : error ? (
          <div style={s.vuoto}>{errMsg(error, 'Errore nel caricamento')}</div>
        ) : data.segnalazioni.length === 0 ? (
          <div style={s.vuoto}>Nessuna segnalazione qui.</div>
        ) : (
          data.segnalazioni.map((x) => {
            const t = TIPI[x.tipo];
            const st = STATI[x.stato];
            const Icona = t.icon;
            return (
              <button key={x.id} style={s.riga} onClick={() => setAperta(x.id)}>
                <span style={{ ...s.iconaTipo, backgroundColor: t.bg, color: t.color }}><Icona size={17} /></span>
                <span style={{ flex: 1, minWidth: 0 }}>
                  <span style={s.rigaTitolo}>{x.titolo}</span>
                  <span style={s.rigaMeta}>
                    {t.label}
                    {x.area ? ` · ${x.area}` : ''}
                    {' · '}{fmtDataOra(x.created_at)}
                    {x.autore ? ` · ${x.autore}` : ''}
                  </span>
                </span>
                <span style={s.rigaDestra}>
                  {(x.n_audio > 0 || x.n_immagini > 0) && (
                    <span style={s.allegatiMini}>
                      {x.n_audio > 0 && <><Mic size={13} /> {x.n_audio}</>}
                      {x.n_immagini > 0 && <><ImageIcon size={13} style={{ marginLeft: '0.3rem' }} /> {x.n_immagini}</>}
                    </span>
                  )}
                  {x.origine && x.origine !== 'admin' && (
                    <span style={{ ...s.badge, backgroundColor: ORIGINI[x.origine].bg, color: ORIGINI[x.origine].color }}>
                      {ORIGINI[x.origine].label}
                    </span>
                  )}
                  {x.priorita !== 'normale' && (
                    <span style={{ ...s.prio, color: PRIORITA[x.priorita].color }}>{PRIORITA[x.priorita].label}</span>
                  )}
                  <span style={{ ...s.badge, backgroundColor: st.bg, color: st.color }}>{st.label}</span>
                </span>
              </button>
            );
          })
        )}
      </div>

      </>)}

      {nuova && <NuovaSegnalazione onChiudi={() => setNuova(false)} onCreata={(id) => { setNuova(false); setAperta(id); }} />}
      {aperta && <DettaglioSegnalazione id={aperta} onChiudi={() => setAperta(null)} />}
    </AdminLayout>
  );
}

// ============================================================
// Nuova segnalazione: testo, audio e immagini in un colpo solo.
// Il task si crea prima, poi gli allegati si caricano uno per volta: se uno
// fallisce il task resta, e l'allegato si puo' riprovare dal dettaglio.
// ============================================================
function NuovaSegnalazione({ onChiudi, onCreata }) {
  const queryClient = useQueryClient();
  const [form, setForm] = useState({ tipo: 'bug', titolo: '', descrizione: '', area: '', priorita: 'normale' });
  const [allegati, setAllegati] = useState([]); // { file, nome, secondi, tipo, url }
  const [errore, setErrore] = useState('');
  const [invio, setInvio] = useState(false);

  useEffect(() => () => allegati.forEach((a) => a.url && URL.revokeObjectURL(a.url)), []); // eslint-disable-line react-hooks/exhaustive-deps

  const aggiungiAudio = (blob, nome, secondi) => {
    setAllegati((a) => [...a, { file: blob, nome, secondi, tipo: 'audio', url: URL.createObjectURL(blob) }]);
  };
  const aggiungiImmagini = (files) => {
    const nuove = Array.from(files || []).filter((f) => f.type.startsWith('image/'))
      .map((f) => ({ file: f, nome: f.name, tipo: 'immagine', url: URL.createObjectURL(f) }));
    setAllegati((a) => [...a, ...nuove].slice(0, 10));
  };
  const togli = (i) => setAllegati((a) => {
    if (a[i].url) URL.revokeObjectURL(a[i].url);
    return a.filter((_, k) => k !== i);
  });

  const invia = async () => {
    if (!form.titolo.trim()) { setErrore('Serve un titolo'); return; }
    setInvio(true);
    setErrore('');
    try {
      const creata = await segnalazioniAPI.crea(form);
      const falliti = [];
      for (const a of allegati) {
        try {
          await segnalazioniAPI.caricaAllegato(creata.id, a.file, a.nome, a.secondi);
        } catch (e) {
          falliti.push(`${a.nome}: ${errMsg(e, 'caricamento non riuscito')}`);
        }
      }
      queryClient.invalidateQueries({ queryKey: ['segnalazioni'] });
      if (falliti.length) {
        window.alert(`Segnalazione creata, ma alcuni allegati non sono stati caricati:\n${falliti.join('\n')}`);
      }
      onCreata(creata.id);
    } catch (e) {
      setErrore(errMsg(e, 'Salvataggio non riuscito'));
      setInvio(false);
    }
  };

  return (
    <div style={s.overlay} onClick={onChiudi}>
      <div style={s.modale} onClick={(e) => e.stopPropagation()}>
        <div style={s.modaleTesta}>
          <h3 style={s.modaleTitolo}>Nuova segnalazione</h3>
          <button style={s.iconaBtn} onClick={onChiudi} aria-label="Chiudi"><X size={18} /></button>
        </div>
        <div style={s.modaleCorpo}>
          <div style={s.tipiScelta}>
            {Object.entries(TIPI).map(([k, t]) => {
              const Icona = t.icon;
              const scelto = form.tipo === k;
              return (
                <button key={k} type="button" onClick={() => setForm({ ...form, tipo: k })}
                  style={{ ...s.tipoBtn, ...(scelto ? { borderColor: t.color, backgroundColor: t.bg } : {}) }}>
                  <Icona size={18} color={t.color} />
                  <span style={{ fontWeight: 700, fontSize: '0.86rem' }}>{t.label}</span>
                  <span style={{ fontSize: '0.74rem', color: '#6B7280' }}>{t.spiega}</span>
                </button>
              );
            })}
          </div>

          <label style={s.label}>Titolo</label>
          <input style={s.input} value={form.titolo} maxLength={200} autoFocus
            placeholder={form.tipo === 'bug' ? 'es. Il timer non si ferma dal telefono' : 'es. Esportare il conto in PDF'}
            onChange={(e) => setForm({ ...form, titolo: e.target.value })} />

          <label style={s.label}>Descrizione</label>
          <textarea style={{ ...s.input, minHeight: '100px', resize: 'vertical' }} value={form.descrizione}
            placeholder={form.tipo === 'bug'
              ? "Cosa stavi facendo, cosa ti aspettavi e cosa e' successo invece"
              : 'Cosa ti servirebbe e perché'}
            onChange={(e) => setForm({ ...form, descrizione: e.target.value })} />

          <div style={s.dueColonne}>
            <div>
              <label style={s.label}>Dove, nell'app</label>
              <input style={s.input} value={form.area} maxLength={100} placeholder="es. Interventi, Conto, app tecnico"
                onChange={(e) => setForm({ ...form, area: e.target.value })} />
            </div>
            <div>
              <label style={s.label}>Priorità</label>
              <select style={s.input} value={form.priorita} onChange={(e) => setForm({ ...form, priorita: e.target.value })}>
                {Object.entries(PRIORITA).map(([k, p]) => <option key={k} value={k}>{p.label}</option>)}
              </select>
            </div>
          </div>

          <label style={s.label}>Spiegalo a voce o con un'immagine</label>
          <div style={{ display: 'flex', gap: '0.6rem', flexWrap: 'wrap', alignItems: 'flex-start' }}>
            <RegistratoreAudio onPronto={aggiungiAudio} disabilitato={invio || allegati.length >= 10} />
            <label style={s.btnImmagine}>
              <ImageIcon size={16} /> Allega immagini
              <input type="file" accept="image/*" multiple style={{ display: 'none' }}
                onChange={(e) => { aggiungiImmagini(e.target.files); e.target.value = ''; }} />
            </label>
          </div>

          {allegati.length > 0 && (
            <div style={s.allegatiLista}>
              {allegati.map((a, i) => (
                <div key={i} style={s.allegatoRiga}>
                  {a.tipo === 'audio'
                    ? <audio controls src={a.url} style={{ height: '34px', flex: 1, minWidth: 0 }} />
                    : <img src={a.url} alt={a.nome} style={s.miniatura} />}
                  {a.tipo === 'immagine' && <span style={s.allegatoNome}>{a.nome}</span>}
                  <button style={s.iconaBtn} onClick={() => togli(i)} aria-label="Togli"><Trash2 size={15} /></button>
                </div>
              ))}
            </div>
          )}

          {errore && <div style={s.errore}>{errore}</div>}
        </div>
        <div style={s.modalePiede}>
          <button style={s.btnSecondario} onClick={onChiudi} disabled={invio}>Annulla</button>
          <button style={s.btnPrimario} onClick={invia} disabled={invio}>
            {invio ? 'Invio in corso...' : 'Invia segnalazione'}
          </button>
        </div>
      </div>
    </div>
  );
}

// Un allegato scaricato dalla rotta autenticata e mostrato da un URL locale.
function AllegatoMedia({ allegato }) {
  const [url, setUrl] = useState(null);
  const [errore, setErrore] = useState(false);
  useEffect(() => {
    let vivo = true;
    let creato = null;
    segnalazioniAPI.scaricaAllegato(allegato.id)
      .then((blob) => { if (!vivo) return; creato = URL.createObjectURL(blob); setUrl(creato); })
      .catch(() => vivo && setErrore(true));
    return () => { vivo = false; if (creato) URL.revokeObjectURL(creato); };
  }, [allegato.id]);

  if (errore) return <span style={s.piccolo}>Allegato non disponibile</span>;
  if (!url) return <span style={s.piccolo}>Caricamento...</span>;
  if (allegato.tipo === 'audio') return <audio controls src={url} style={{ height: '36px', width: '100%', maxWidth: '360px' }} />;
  return (
    <a href={url} target="_blank" rel="noreferrer">
      <img src={url} alt={allegato.nome_originale || 'immagine'} style={s.immagineGrande} />
    </a>
  );
}

// ============================================================
// Dettaglio: contenuto, allegati, cambio di stato e storia.
// ============================================================
function DettaglioSegnalazione({ id, onChiudi }) {
  const queryClient = useQueryClient();
  const chiave = ['segnalazione', id];
  const [commento, setCommento] = useState('');
  const [nuovoStato, setNuovoStato] = useState('');
  const [errore, setErrore] = useState('');

  const { data: x, isLoading, error } = useQuery({ queryKey: chiave, queryFn: () => segnalazioniAPI.detail(id) });

  const aggiorna = () => {
    setErrore('');
    queryClient.invalidateQueries({ queryKey: chiave });
    queryClient.invalidateQueries({ queryKey: ['segnalazioni'] });
  };
  const fallito = (e) => setErrore(errMsg(e, 'Operazione non riuscita'));

  const cambia = useMutation({
    mutationFn: () => segnalazioniAPI.cambiaStato(id, nuovoStato, commento),
    onSuccess: () => { setCommento(''); setNuovoStato(''); aggiorna(); },
    onError: fallito,
  });
  const commenta = useMutation({
    mutationFn: () => segnalazioniAPI.commenta(id, commento),
    onSuccess: () => { setCommento(''); aggiorna(); },
    onError: fallito,
  });
  const allega = useMutation({
    mutationFn: ({ file, nome, secondi }) => segnalazioniAPI.caricaAllegato(id, file, nome, secondi),
    onSuccess: aggiorna,
    onError: fallito,
  });
  const togliAllegato = useMutation({
    mutationFn: (aid) => segnalazioniAPI.eliminaAllegato(aid),
    onSuccess: aggiorna,
    onError: fallito,
  });
  const elimina = useMutation({
    mutationFn: () => segnalazioniAPI.elimina(id),
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ['segnalazioni'] }); onChiudi(); },
    onError: fallito,
  });
  const cambiaPriorita = useMutation({
    mutationFn: (priorita) => segnalazioniAPI.modifica(id, { priorita }),
    onSuccess: aggiorna,
    onError: fallito,
  });

  const occupato = cambia.isPending || commenta.isPending || allega.isPending;

  return (
    <div style={s.overlay} onClick={onChiudi}>
      <div style={{ ...s.modale, maxWidth: '680px' }} onClick={(e) => e.stopPropagation()}>
        <div style={s.modaleTesta}>
          <h3 style={s.modaleTitolo}>{x ? x.titolo : 'Segnalazione'}</h3>
          <button style={s.iconaBtn} onClick={onChiudi} aria-label="Chiudi"><X size={18} /></button>
        </div>
        <div style={s.modaleCorpo}>
          {isLoading ? <div style={s.vuoto}>Caricamento...</div> : error ? <div style={s.vuoto}>{errMsg(error, 'Errore')}</div> : (
            <>
              <div style={s.testataBadge}>
                <span style={{ ...s.badge, backgroundColor: TIPI[x.tipo].bg, color: TIPI[x.tipo].color }}>{TIPI[x.tipo].label}</span>
                <span style={{ ...s.badge, backgroundColor: STATI[x.stato].bg, color: STATI[x.stato].color }}>{STATI[x.stato].label}</span>
                <select style={{ ...s.selectPiccolo, color: PRIORITA[x.priorita].color }} value={x.priorita}
                  onChange={(e) => cambiaPriorita.mutate(e.target.value)}>
                  {Object.entries(PRIORITA).map(([k, p]) => <option key={k} value={k}>Priorità {p.label.toLowerCase()}</option>)}
                </select>
                {x.area && <span style={s.piccolo}>· {x.area}</span>}
              </div>
              <div style={s.piccolo}>
                Aperta da {x.autore || '—'}{x.origine && x.origine !== 'admin' ? ` (${ORIGINI[x.origine].label.toLowerCase()})` : ''} il {fmtDataOra(x.created_at)}
              </div>
              {x.origine && x.origine !== 'admin' && (
                <div style={s.avvisoAutore}>
                  L'ha aperta {x.origine === 'cliente' ? 'un cliente' : 'un tecnico'}: legge i commenti e la
                  risposta, e riceve un avviso a ogni cambio di stato. Nei suoi schermi l'ufficio compare come «Assistenza».
                </div>
              )}

              {x.risposta && (
                <div style={{ ...s.risposta, borderColor: STATI[x.stato].color }}>
                  <strong>{x.stato === 'rifiutata' ? 'Motivo del rifiuto' : 'Risposta'}:</strong> {x.risposta}
                </div>
              )}

              {x.descrizione && <p style={s.descrizione}>{x.descrizione}</p>}

              <h4 style={s.sezione}><Paperclip size={14} /> Allegati</h4>
              {x.allegati.length === 0 && <div style={s.piccolo}>Nessun allegato.</div>}
              <div style={s.allegatiLista}>
                {x.allegati.map((a) => (
                  <div key={a.id} style={s.allegatoRiga}>
                    <div style={{ flex: 1, minWidth: 0 }}><AllegatoMedia allegato={a} /></div>
                    <button style={s.iconaBtn} aria-label="Elimina allegato"
                      onClick={() => window.confirm('Eliminare questo allegato?') && togliAllegato.mutate(a.id)}>
                      <Trash2 size={15} />
                    </button>
                  </div>
                ))}
              </div>
              {x.allegati.length < 10 && (
                <div style={{ display: 'flex', gap: '0.6rem', flexWrap: 'wrap', marginTop: '0.5rem' }}>
                  <RegistratoreAudio disabilitato={occupato} onPronto={(file, nome, secondi) => allega.mutate({ file, nome, secondi })} />
                  <label style={s.btnImmagine}>
                    <ImageIcon size={16} /> Aggiungi immagine
                    <input type="file" accept="image/*" style={{ display: 'none' }}
                      onChange={(e) => { const f = e.target.files?.[0]; if (f) allega.mutate({ file: f, nome: f.name }); e.target.value = ''; }} />
                  </label>
                </div>
              )}

              <h4 style={s.sezione}>Aggiorna</h4>
              <div style={s.statiScelta}>
                {Object.entries(STATI).filter(([k]) => k !== x.stato).map(([k, st]) => (
                  <button key={k} type="button" onClick={() => setNuovoStato(nuovoStato === k ? '' : k)}
                    style={{ ...s.statoBtn, ...(nuovoStato === k ? { backgroundColor: st.bg, color: st.color, borderColor: st.color } : {}) }}>
                    {st.label}
                  </button>
                ))}
              </div>
              <textarea style={{ ...s.input, minHeight: '70px', marginTop: '0.5rem' }} value={commento}
                placeholder={nuovoStato === 'rifiutata' ? 'Perché viene rifiutata (obbligatorio)'
                  : nuovoStato === 'completata' ? 'Cosa è stato fatto (facoltativo)'
                    : 'Un commento (facoltativo se cambi stato)'}
                onChange={(e) => setCommento(e.target.value)} />
              <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end', marginTop: '0.5rem', flexWrap: 'wrap' }}>
                {!nuovoStato && (
                  <button style={s.btnSecondario} disabled={occupato || !commento.trim()} onClick={() => commenta.mutate()}>
                    <Send size={14} /> Commenta
                  </button>
                )}
                {nuovoStato && (
                  <button style={s.btnPrimario}
                    disabled={occupato || (nuovoStato === 'rifiutata' && !commento.trim())}
                    onClick={() => cambia.mutate()}>
                    Segna come «{STATI[nuovoStato].label}»
                  </button>
                )}
              </div>
              {errore && <div style={s.errore}>{errore}</div>}

              <h4 style={s.sezione}>Storia</h4>
              <div style={s.storia}>
                {x.storico.map((h) => (
                  <div key={h.id} style={s.storiaRiga}>
                    <div style={s.piccolo}>{fmtDataOra(h.created_at)} · {h.utente || '—'}</div>
                    {h.stato_nuovo && (
                      <div style={{ fontSize: '0.85rem' }}>
                        {h.stato_precedente
                          ? <>Da <strong>{STATI[h.stato_precedente]?.label}</strong> a <strong>{STATI[h.stato_nuovo]?.label}</strong></>
                          : <>Creata</>}
                      </div>
                    )}
                    {h.commento && <div style={s.storiaCommento}>{h.commento}</div>}
                  </div>
                ))}
              </div>

              {x.stato === 'nuova' && (
                <button style={s.btnElimina} onClick={() => window.confirm('Eliminare la segnalazione e i suoi allegati?') && elimina.mutate()}>
                  <Trash2 size={14} /> Elimina segnalazione
                </button>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

const s = {
  sezioni: { display: 'flex', gap: '0.25rem', borderBottom: '2px solid #E5E7EB', marginBottom: '1rem', flexWrap: 'wrap' },
  sezioneBtn: { border: 'none', background: 'none', padding: '0.6rem 1rem', fontSize: '0.92rem', fontWeight: 600, color: '#6B7280', cursor: 'pointer', borderBottom: '2px solid transparent', marginBottom: '-2px' },
  sezioneAttiva: { color: '#7C3AED', borderBottomColor: '#8B5CF6' },
  avvisoAutore: { marginTop: '0.6rem', padding: '0.55rem 0.75rem', backgroundColor: '#EFF6FF', color: '#1E40AF', borderRadius: '8px', fontSize: '0.8rem', lineHeight: 1.5 },
  intro: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '1rem', flexWrap: 'wrap', backgroundColor: 'white', padding: '1rem 1.25rem', borderRadius: '12px', boxShadow: '0 1px 3px rgba(0,0,0,0.04)', marginBottom: '1rem' },
  introTesto: { margin: 0, fontSize: '0.86rem', color: '#4B5563', lineHeight: 1.5, flex: '1 1 320px' },
  linguette: { display: 'flex', gap: '0.4rem', flexWrap: 'wrap', marginBottom: '0.8rem' },
  linguetta: { display: 'inline-flex', alignItems: 'center', gap: '0.4rem', padding: '0.45rem 0.85rem', border: '1px solid #E5E7EB', backgroundColor: 'white', borderRadius: '999px', fontSize: '0.84rem', fontWeight: 600, color: '#4B5563', cursor: 'pointer', minHeight: '36px' },
  linguettaAttiva: { backgroundColor: '#8B5CF6', borderColor: '#8B5CF6', color: 'white' },
  conteggio: { fontSize: '0.72rem', backgroundColor: 'rgba(0,0,0,0.08)', borderRadius: '999px', padding: '0.05rem 0.45rem' },
  filtri: { display: 'flex', gap: '0.6rem', flexWrap: 'wrap', marginBottom: '0.8rem' },
  cercaBox: { flex: '1 1 260px', display: 'flex', alignItems: 'center', gap: '0.45rem', backgroundColor: 'white', border: '1px solid #E5E7EB', borderRadius: '10px', padding: '0 0.75rem' },
  cercaInput: { flex: 1, border: 'none', outline: 'none', padding: '0.6rem 0', fontSize: '0.9rem', fontFamily: 'inherit', minWidth: 0 },
  select: { padding: '0.55rem 0.7rem', border: '1px solid #E5E7EB', borderRadius: '10px', fontSize: '0.88rem', backgroundColor: 'white' },
  elenco: { backgroundColor: 'white', borderRadius: '12px', boxShadow: '0 1px 3px rgba(0,0,0,0.04)', overflow: 'hidden' },
  riga: { display: 'flex', alignItems: 'center', gap: '0.8rem', width: '100%', padding: '0.85rem 1rem', border: 'none', borderBottom: '1px solid #F3F4F6', background: 'white', cursor: 'pointer', textAlign: 'left', fontFamily: 'inherit', flexWrap: 'wrap' },
  iconaTipo: { width: '36px', height: '36px', borderRadius: '10px', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 },
  rigaTitolo: { display: 'block', fontWeight: 600, fontSize: '0.93rem', color: '#111827', overflow: 'hidden', textOverflow: 'ellipsis' },
  rigaMeta: { display: 'block', fontSize: '0.77rem', color: '#6B7280', marginTop: '0.15rem' },
  rigaDestra: { display: 'inline-flex', alignItems: 'center', gap: '0.6rem', flexWrap: 'wrap', marginLeft: 'auto' },
  allegatiMini: { display: 'inline-flex', alignItems: 'center', gap: '0.2rem', fontSize: '0.77rem', color: '#6B7280' },
  prio: { fontSize: '0.76rem', fontWeight: 700 },
  badge: { display: 'inline-block', padding: '0.2rem 0.6rem', borderRadius: '6px', fontSize: '0.76rem', fontWeight: 700, whiteSpace: 'nowrap' },
  vuoto: { padding: '2rem', textAlign: 'center', color: '#6B7280' },
  overlay: { position: 'fixed', inset: 0, backgroundColor: 'rgba(17,24,39,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem', zIndex: 1000 },
  modale: { backgroundColor: 'white', borderRadius: '14px', width: '100%', maxWidth: '600px', maxHeight: '92vh', display: 'flex', flexDirection: 'column' },
  modaleTesta: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '0.5rem', padding: '1rem 1.25rem', borderBottom: '1px solid #E5E7EB' },
  modaleTitolo: { margin: 0, fontSize: '1.05rem', fontWeight: 700 },
  modaleCorpo: { padding: '1.1rem 1.25rem', overflowY: 'auto' },
  modalePiede: { display: 'flex', justifyContent: 'flex-end', gap: '0.6rem', padding: '0.9rem 1.25rem', borderTop: '1px solid #E5E7EB' },
  tipiScelta: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '0.5rem' },
  tipoBtn: { display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: '0.25rem', padding: '0.7rem', border: '2px solid #E5E7EB', borderRadius: '10px', background: 'white', cursor: 'pointer', textAlign: 'left', fontFamily: 'inherit' },
  label: { display: 'block', fontSize: '0.8rem', fontWeight: 600, color: '#374151', marginTop: '0.85rem', marginBottom: '0.3rem' },
  input: { width: '100%', padding: '0.6rem 0.7rem', border: '1px solid #E5E7EB', borderRadius: '10px', fontSize: '0.92rem', boxSizing: 'border-box', fontFamily: 'inherit', backgroundColor: 'white' },
  dueColonne: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '0 0.75rem' },
  btnImmagine: { display: 'inline-flex', alignItems: 'center', gap: '0.45rem', padding: '0.55rem 0.95rem', backgroundColor: '#F3F4F6', color: '#374151', borderRadius: '10px', fontSize: '0.87rem', fontWeight: 600, cursor: 'pointer', minHeight: '40px', boxSizing: 'border-box' },
  allegatiLista: { display: 'flex', flexDirection: 'column', gap: '0.5rem', marginTop: '0.6rem' },
  allegatoRiga: { display: 'flex', alignItems: 'center', gap: '0.6rem', padding: '0.4rem 0.5rem', border: '1px solid #F3F4F6', borderRadius: '10px' },
  allegatoNome: { flex: 1, fontSize: '0.82rem', color: '#4B5563', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  miniatura: { width: '48px', height: '48px', objectFit: 'cover', borderRadius: '6px' },
  immagineGrande: { maxWidth: '100%', maxHeight: '260px', borderRadius: '8px', border: '1px solid #E5E7EB' },
  errore: { marginTop: '0.75rem', padding: '0.6rem 0.75rem', backgroundColor: '#FEE2E2', color: '#991B1B', borderRadius: '8px', fontSize: '0.85rem' },
  btnPrimario: { display: 'inline-flex', alignItems: 'center', gap: '0.4rem', padding: '0.55rem 1rem', backgroundColor: '#8B5CF6', color: 'white', border: 'none', borderRadius: '10px', fontSize: '0.9rem', fontWeight: 600, cursor: 'pointer', minHeight: '40px' },
  btnSecondario: { display: 'inline-flex', alignItems: 'center', gap: '0.4rem', padding: '0.55rem 1rem', backgroundColor: '#F3F4F6', color: '#374151', border: 'none', borderRadius: '10px', fontSize: '0.9rem', fontWeight: 600, cursor: 'pointer', minHeight: '40px' },
  btnElimina: { display: 'inline-flex', alignItems: 'center', gap: '0.35rem', marginTop: '1.25rem', padding: '0.45rem 0.8rem', backgroundColor: 'white', color: '#B91C1C', border: '1px solid #FECACA', borderRadius: '8px', fontSize: '0.82rem', fontWeight: 600, cursor: 'pointer' },
  iconaBtn: { border: 'none', background: 'none', cursor: 'pointer', color: '#6B7280', display: 'inline-flex', padding: '0.3rem' },
  testataBadge: { display: 'flex', alignItems: 'center', gap: '0.45rem', flexWrap: 'wrap', marginBottom: '0.35rem' },
  selectPiccolo: { padding: '0.2rem 0.4rem', border: '1px solid #E5E7EB', borderRadius: '6px', fontSize: '0.78rem', fontWeight: 700, backgroundColor: 'white' },
  piccolo: { fontSize: '0.78rem', color: '#6B7280' },
  risposta: { marginTop: '0.8rem', padding: '0.65rem 0.8rem', borderLeft: '4px solid', backgroundColor: '#F9FAFB', borderRadius: '6px', fontSize: '0.87rem', lineHeight: 1.5 },
  descrizione: { fontSize: '0.9rem', lineHeight: 1.6, color: '#1F2937', whiteSpace: 'pre-wrap', margin: '0.9rem 0 0' },
  sezione: { display: 'flex', alignItems: 'center', gap: '0.35rem', fontSize: '0.86rem', fontWeight: 700, margin: '1.3rem 0 0.5rem', color: '#111827' },
  statiScelta: { display: 'flex', gap: '0.4rem', flexWrap: 'wrap' },
  statoBtn: { padding: '0.4rem 0.75rem', border: '1px solid #E5E7EB', borderRadius: '999px', background: 'white', fontSize: '0.82rem', fontWeight: 600, color: '#4B5563', cursor: 'pointer', minHeight: '34px' },
  storia: { display: 'flex', flexDirection: 'column', gap: '0.6rem', borderLeft: '2px solid #E5E7EB', paddingLeft: '0.85rem' },
  storiaRiga: {},
  storiaCommento: { fontSize: '0.86rem', color: '#1F2937', whiteSpace: 'pre-wrap', marginTop: '0.15rem' },
};
